import { Icon, MarkerLabel, Point, Size } from 'src/app/models/custom-marker.model';
import { Coords } from 'src/app/swagger-api';
import MapSettings from '../../map.settings';
import { CustomMarkerBuilder } from './custom-marker.builder';
import { BucketTooltip } from '../../map.interfaces';
import { SearchCategories } from '../../../search/search.enum';

export abstract class AggregatedMarkerBuilder extends CustomMarkerBuilder {
  public setCounterLabel(itemsCount: number): void {
    this.marker.label = this.configureMarkerLabel(this.getLabelColor(), itemsCount);
  }
  public setLocationLiteral(locationLiteral: string): void {
    this.marker.locationLiteral = locationLiteral;
  }

  public toggleIsAggregatedFlag(): void {
    this.marker.isAggregated = true;
  }

  public setTooltips(tooltips: BucketTooltip[]): void {
    this.marker.tooltips = tooltips;
  }

  public setItemsCount(itemsCount: number): void {
    this.marker.itemsCount = itemsCount;
  }

  public setItemsKind(): void {
    this.marker.itemKind = this.getMarkerKind();
  }

  protected abstract getLabelColor(): string;

  protected abstract getMarkerKind(): string;

  protected getMarkerId(coords: Coords = { lon: 0, lat: 0 }): string {
    return `${coords.lat}_${coords.lon}`;
  }

  protected override formIcon(scaledSize: Size): Icon {
    return {
      url: this.getIconUrl(),
      scaledSize,
      labelOrigin: this.configureLabelOrigin(scaledSize.width),
    };
  }

  protected override getIconSize(count: number): number {
    let size: number = super.getIconSize(count);

    if (count) {
      size =
        count >= MapSettings.BIG_MARKER_COUNT
          ? MapSettings.BIG_MARKER_SIZE
          : count >= MapSettings.MEDIUM_MARKER_COUNT
            ? MapSettings.MEDIUM_MARKER_SIZE
            : size;
    }

    return size;
  }

  /**
   * @description Configures the marker label's properties depend on
   *              aggregated count value.
   * @param { string } color
   * @param { number } count
   * @returns { google.maps.MarkerLabel }
   */
  protected configureMarkerLabel(color: string, count: number): MarkerLabel {
    return {
      color,
      text: `${count}`,
      fontWeight: 'bold',
    };
  }

  /**
   * @description Configures the marker icon label depend on
   *              aggregated count value.
   * @param { number } scaledSizeWidth
   * @returns { google.maps.Point }
   */
  protected configureLabelOrigin(scaledSizeWidth: number): Point {
    return {
      ...{
        [MapSettings.SMALL_MARKER_SIZE]: { x: 15, y: 40 },
        [MapSettings.MEDIUM_MARKER_SIZE]: { x: 22.5, y: 52 },
        [MapSettings.BIG_MARKER_SIZE]: { x: 29, y: 64 },
      }[scaledSizeWidth],
    };
  }
}

export class AggregatedCommunityMarkerBuilder extends AggregatedMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.COMMUNITY_MARKER_ICON_URL;
  }

  public override getMarkerKind(): string {
    return SearchCategories.COMMUNITIES;
  }

  protected getLabelColor(): string {
    return MapSettings.COMMUNITY_MARKER_ICON_COLOR;
  }
}

export class AggregatedProjectMarkerBuilder extends AggregatedMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.PROJECT_MARKER_ICON_URL;
  }

  public override getMarkerKind(): string {
    return SearchCategories.PROJECTS;
  }

  protected getLabelColor(): string {
    return MapSettings.PROJECT_MARKER_ICON_COLOR;
  }
}

export class AggregatedOrganizationMarkerBuilder extends AggregatedMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.ORGANIZATION_MARKER_ICON_URL;
  }

  public override getMarkerKind(): string {
    return SearchCategories.ORGANIZATIONS;
  }

  protected getLabelColor(): string {
    return MapSettings.ORGANIZATION_MARKER_ICON_COLOR;
  }
}

export class AggregatedPatientMarkerBuilder extends AggregatedMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.PATIENT_MARKER_ICON_URL;
  }

  public override getMarkerKind(): string {
    return SearchCategories.PATIENTS;
  }

  protected getLabelColor(): string {
    return MapSettings.PATIENT_MARKER_ICON_COLOR;
  }
}

export class AggregatedDoctorMarkerBuilder extends AggregatedMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.DOCTOR_MARKER_ICON_URL;
  }

  public override getMarkerKind(): string {
    return SearchCategories.DOCTORS;
  }

  protected getLabelColor(): string {
    return MapSettings.DOCTOR_MARKER_ICON_COLOR;
  }
}

export class AggregatedMedStaffMarkerBuilder extends AggregatedMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.MED_STAFF_MARKER_ICON_URL;
  }

  public override getMarkerKind(): string {
    return SearchCategories.MED_STAFF;
  }

  protected getLabelColor(): string {
    return MapSettings.MED_STAFF_MARKER_ICON_COLOR;
  }
}

export class AggregatedResearcherMarkerBuilder extends AggregatedMarkerBuilder {
  public override getIconUrl(): string {
    return MapSettings.RESEARCHER_MARKER_ICON_URL;
  }

  public override getMarkerKind(): string {
    return SearchCategories.RESEARCHERS;
  }

  protected getLabelColor(): string {
    return MapSettings.RESEARCHER_MARKER_ICON_COLOR;
  }
}
